import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Registration, FamilyMember, VillaPreference,
  StepGroupLeaderData, StepSpecialNeedsData,
  AgeGroup,
} from '../types/registration';

interface RegistrationFormState {
  // Server-side registration (from API)
  registration: Registration | null;
  setRegistration: (reg: Registration | null) => void;

  // Current step (0-indexed: 0=GroupLeader, 1=FamilyMembers, 2=VillaPreference, 3=SpecialNeeds, 4=Review)
  currentStep: number;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1: Group Leader Info
  groupLeaderData: StepGroupLeaderData;
  setGroupLeaderData: (data: Partial<StepGroupLeaderData>) => void;

  // Step 2: Family Members
  familyMembers: Omit<FamilyMember, 'id' | 'sortOrder'>[];
  setFamilyMembers: (members: Omit<FamilyMember, 'id' | 'sortOrder'>[]) => void;
  addFamilyMember: () => void;
  removeFamilyMember: (index: number) => void;
  updateFamilyMember: (index: number, data: Partial<FamilyMember>) => void;

  // Step 3: Villa Preference
  villaPreference: VillaPreference;
  setVillaPreference: (data: Partial<VillaPreference>) => void;

  // Step 4: Special Needs (per member)
  specialNeeds: StepSpecialNeedsData;
  setSpecialNeeds: (data: StepSpecialNeedsData) => void;

  // Draft save tracking
  lastSavedAt: string | null;
  setLastSavedAt: (timestamp: string) => void;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;

  // Reset form state (for new registration)
  resetForm: () => void;

  // Hydrate form state from server registration
  hydrateFromRegistration: (reg: Registration) => void;
}

const TOTAL_STEPS = 5;

const initialGroupLeader: StepGroupLeaderData = { isGroupLeader: true, phone: '', familyConnection: '' };
const initialVillaPreference: VillaPreference = {
  villaTypePreference: null, flexibilityNotes: null, roomConfigNotes: null,
};
const initialSpecialNeeds: StepSpecialNeedsData = { memberNeeds: [] };

export const useRegistrationStore = create<RegistrationFormState>()(
  persist(
    (set, _get) => ({
      registration: null,
      setRegistration: (reg) => set({ registration: reg }),

      currentStep: 0,
      setCurrentStep: (step) => set({ currentStep: Math.min(Math.max(step, 0), TOTAL_STEPS - 1) }),
      nextStep: () => set((s) => ({ currentStep: Math.min(s.currentStep + 1, TOTAL_STEPS - 1) })),
      prevStep: () => set((s) => ({ currentStep: Math.max(s.currentStep - 1, 0) })),

      groupLeaderData: initialGroupLeader,
      setGroupLeaderData: (data) => set((s) => ({ groupLeaderData: { ...s.groupLeaderData, ...data } })),

      familyMembers: [],
      setFamilyMembers: (members) => set({ familyMembers: members }),
      addFamilyMember: () => set((s) => ({
        familyMembers: [...s.familyMembers, {
          firstName: '', lastName: '', ageGroup: 'adult' as AgeGroup,
          relationship: '', dietaryRestrictions: [],
          foodAllergies: null, accessibilityNeeds: null, profileImageUrl: null,
          email: null,
        }],
      })),
      removeFamilyMember: (index) => set((s) => ({
        familyMembers: s.familyMembers.filter((_, i) => i !== index),
      })),
      updateFamilyMember: (index, data) => set((s) => ({
        familyMembers: s.familyMembers.map((m, i) => i === index ? { ...m, ...data } : m),
      })),

      villaPreference: initialVillaPreference,
      setVillaPreference: (data) => set((s) => ({
        villaPreference: { ...s.villaPreference, ...data },
      })),

      specialNeeds: initialSpecialNeeds,
      setSpecialNeeds: (data) => set({ specialNeeds: data }),

      lastSavedAt: null,
      setLastSavedAt: (timestamp) => set({ lastSavedAt: timestamp }),
      isSaving: false,
      setIsSaving: (saving) => set({ isSaving: saving }),

      resetForm: () => set({
        registration: null,
        currentStep: 0,
        groupLeaderData: initialGroupLeader,
        familyMembers: [],
        villaPreference: initialVillaPreference,
        specialNeeds: initialSpecialNeeds,
        lastSavedAt: null,
      }),

      hydrateFromRegistration: (reg) => set({
        registration: reg,
        groupLeaderData: {
          isGroupLeader: reg.isGroupLeader,
          phone: reg.phone || '',
          familyConnection: reg.familyConnection || '',
        },
        familyMembers: reg.familyMembers.map((fm) => {
          // dietaryRestrictions may be a JSON string from the DB — parse if needed
          const dr = typeof fm.dietaryRestrictions === 'string'
            ? (() => { try { return JSON.parse(fm.dietaryRestrictions); } catch { return []; } })()
            : fm.dietaryRestrictions ?? [];
          return {
            firstName: fm.firstName,
            lastName: fm.lastName,
            ageGroup: fm.ageGroup,
            relationship: fm.relationship,
            dietaryRestrictions: Array.isArray(dr) ? dr : [],
            foodAllergies: fm.foodAllergies,
            accessibilityNeeds: fm.accessibilityNeeds,
            profileImageUrl: fm.profileImageUrl,
            email: fm.email || null,
          };
        }),
        villaPreference: reg.villaPreference || initialVillaPreference,
        specialNeeds: {
          memberNeeds: reg.familyMembers.map((fm) => {
            const dr = typeof fm.dietaryRestrictions === 'string'
              ? (() => { try { return JSON.parse(fm.dietaryRestrictions); } catch { return []; } })()
              : fm.dietaryRestrictions ?? [];
            return {
              dietaryRestrictions: Array.isArray(dr) ? dr : [],
              foodAllergies: fm.foodAllergies || '',
              accessibilityNeeds: fm.accessibilityNeeds || '',
            };
          }),
        },
      }),
    }),
    {
      name: 'reunion-registration-draft',
      storage: createJSONStorage(() => localStorage),
      // Only persist form data, not server state or saving flags
      partialize: (state) => ({
        currentStep: state.currentStep,
        groupLeaderData: state.groupLeaderData,
        familyMembers: state.familyMembers,
        villaPreference: state.villaPreference,
        specialNeeds: state.specialNeeds,
      }),
    }
  )
);
